/*
 * Render a LabelingStrategyInput for a single schema with all of the options.
 *
 * LabelingStrategyInput supports multiple schemas and sets of options. This component
 *   - handles the simple case so the caller doesn't need to specify labeling strategy
 *       - all filtered labeling strategies if schema selected
 *       - [IDS_FROM_NAMES] otherwise
 *   - selects a valid labeling strategy by default if it is not selected
 *   - is hidden if there is only <= 1 option to choose from
 */
import {gql} from '@apollo/client';
import React from 'react';
import _ from 'underscore';

import {LabelingStrategy, ORDERED_LABELING_STRATEGIES} from 'constants/RegistryConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  SchemaLabelingStrategiesQuery,
  SchemaLabelingStrategiesQueryVariables,
} from 'registry/labeling-strategies/__generated/SchemaLabelingStrategiesQuery';
import {
  filterLabelingStrategies,
  getDefaultLabelingStrategy,
} from 'registry/labeling-strategies/LabelingStrategiesUtil';

import LabelingStrategyInput from './LabelingStrategyInput';

export interface SchemaLabelingStrategyInputProps {
  disabled?: boolean;
  // The possible labeling strategies we should show
  labelingStrategies?: Array<LabelingStrategy> | null;
  bannedLabelingStrategies?: Array<LabelingStrategy>;

  // The selected labeling strategy
  labelingStrategy?: LabelingStrategy | null;
  labelStyle?: string;
  onChange: (labelingStrategy: LabelingStrategy) => void;

  // The schema from which we should pull labeling strategies from
  tagSchemaId?: string | null;
}

export const SCHEMA_LABELING_STRATEGY_INPUT_QUERY = gql`
  query SchemaLabelingStrategiesQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: REGISTRY) {
    tagSchema(id: $tagSchemaId) {
      ...TagSchemaGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('TagSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ConnectedProps {
  labelingStrategies: Array<LabelingStrategy>;
}

type SchemaLabelingStrategyInputAndConnectedProps = Omit<
  SchemaLabelingStrategyInputProps & ConnectedProps,
  'bannedLabelingStrategies'
>;

export class SchemaLabelingStrategyInput extends React.Component<SchemaLabelingStrategyInputAndConnectedProps> {
  static defaultProps = {
    disabled: false,
  };

  override UNSAFE_componentWillMount(): void {
    if (!this.props.disabled) {
      this._setDefaultLabelingStrategy(this.props);
    }
  }

  override UNSAFE_componentWillReceiveProps(nextProps: SchemaLabelingStrategyInputAndConnectedProps): void {
    if (!this.props.disabled) {
      this._setDefaultLabelingStrategy(nextProps);
    }
  }

  _setDefaultLabelingStrategy(props: SchemaLabelingStrategyInputAndConnectedProps): void {
    const {tagSchemaId, labelingStrategy, labelingStrategies, onChange} = props;
    const tagSchemaIds = _.compact([tagSchemaId]);
    const isValidOptionChosen = labelingStrategy && labelingStrategies.includes(labelingStrategy);
    if (isValidOptionChosen || _.isEmpty(labelingStrategies)) {
      return;
    }
    onChange(getDefaultLabelingStrategy(labelingStrategies, tagSchemaIds));
  }

  override render(): JSX.Element {
    const {disabled, labelingStrategies, labelingStrategy, labelStyle, onChange, tagSchemaId} = this.props;
    const tagSchemaIds = _.compact([tagSchemaId]);
    return (
      <LabelingStrategyInput
        disabled={Boolean(disabled)}
        labelingStrategies={labelingStrategies}
        labelingStrategy={
          labelingStrategy != null
            ? labelingStrategy
            : getDefaultLabelingStrategy(labelingStrategies, tagSchemaIds)
        }
        labelStyle={labelStyle}
        onChange={onChange}
        tagSchemaIds={tagSchemaIds}
      />
    );
  }
}

export default function QueryingSchemaLabelingStrategyInput(
  props: SchemaLabelingStrategyInputProps
): JSX.Element {
  if (props.tagSchemaId == null) {
    return <LabelingStrategyInput {...props} labelingStrategies={props.labelingStrategies || null} />;
  } else {
    return (
      <BenchlingQuery<SchemaLabelingStrategiesQuery, SchemaLabelingStrategiesQueryVariables>
        query={SCHEMA_LABELING_STRATEGY_INPUT_QUERY}
        renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
        variables={{tagSchemaId: props.tagSchemaId}}
      >
        {(data) => {
          const {bannedLabelingStrategies, ...childProps} = props;
          if (isInaccessible(data.tagSchema) || data.tagSchema.labelingStrategies.length === 0) {
            return <SchemaLabelingStrategyInput {...childProps} labelingStrategies={[]} />;
          } else {
            const allowedLabelingStrategies = data.tagSchema.labelingStrategies.filter(
              (strategy) =>
                props.bannedLabelingStrategies == null || !props.bannedLabelingStrategies.includes(strategy)
            );
            return (
              <SchemaLabelingStrategyInput
                {...childProps}
                labelingStrategies={filterLabelingStrategies(ORDERED_LABELING_STRATEGIES, {
                  labelingStrategies: allowedLabelingStrategies,
                })}
              />
            );
          }
        }}
      </BenchlingQuery>
    );
  }
}
